import type { LevelDef, Loadout } from '@full-elevator/sim';
import { floorName, groundOf } from '../floorNames.js';
import type { Size } from '../phone.js';
import { recentresAfter } from '../phone.js';
import type { LiftLook } from '../view/liftParts.js';
import { liftLookOf } from '../view/liftParts.js';
import { callButton, clear, coinIcon, el, icon } from './dom.js';
import { crownSvg, entranceSvg } from './facades.js';
import type { HomeBuilding, HomeFloor, HomeModel } from './homeModel.js';
import { previewFor } from './homeModel.js';
import { medalSvg } from './medal.js';
import { personSvg } from './personSvg.js';
import { roomSvg } from './rooms.js';
import type { Street } from './street.js';
import { buildStreet } from './street.js';
import type { SwipeOptions } from './swipe.js';
import { attachSwipe } from './swipe.js';
import { DRIP, METRO, TUNNEL_ART, UTILITIES, WORM_ART } from './underground.js';

/**
 * The home screen is the building (ADR-0019).
 *
 * One building at a time, in cutaway: one floor per shift, played floors lit
 * with their stars, the next one lit with the lift parked at it — doors open,
 * its passengers already waiting, and Play on the floor itself — and the floors
 * not reached yet dark. Every building can be paged to, a later one as a dark
 * silhouette with nothing on it to tap until it is reached, which keeps this a
 * sequence and not a selection screen.
 *
 * A floor here is a *shift*, not a stop: it wears a brass door plaque, never
 * the round colour badge the game gives a floor, so "33" is never read as a
 * destination.
 */
export interface HomeView {
  readonly model: HomeModel;
  /** Which building is on screen. */
  readonly viewing: number;
  readonly coins: number;
  readonly garageReady: boolean;
  /** The first part is waiting to be fitted together (ADR-0041): Upgrade calls for it. */
  readonly lessonDue?: boolean;
  /** What is fitted to the lift of the building on screen: its own (ADR-0027). */
  readonly loadout: Loadout;
  readonly stamp: string;
  /** Set when arriving from a neighbouring building: the side it slides in from. */
  readonly enter?: 'from-left' | 'from-right' | undefined;
}

export interface HomeActions {
  readonly play: (level: LevelDef) => void;
  readonly view: (building: number) => void;
  readonly garage: () => void;
  readonly settings: () => void;
}

/**
 * What stays on the home screen while the player pages between buildings: the
 * sky, the street, and the swipe that drives them. Built once per screen, so
 * the people and the traffic outside never jump when the building changes.
 */
interface Frame {
  header: HTMLElement;
  readonly stage: HTMLElement;
  readonly street: Street;
  readonly stamp: HTMLElement;
  readonly swipe: SwipeOptions;
  scroll: HTMLElement | null;
}

const frames = new WeakMap<HTMLElement, Frame>();

function frameFor(screen: HTMLElement): Frame {
  const existing = frames.get(screen);
  if (existing && existing.stage.parentElement === screen) return existing;
  clear(screen);
  const street = buildStreet();
  const stamp = el('div', { class: 'home-stamp' });
  const stage = el('div', { class: 'home-stage' }, [street.sky, street.street, stamp]);
  const header = el('header');
  screen.append(header, stage);
  const frame: Frame = {
    header,
    stage,
    street,
    stamp,
    scroll: null,
    swipe: {
      canPrev: false,
      canNext: false,
      target: () => frame.scroll,
      drag: (offset) => street.drag(offset),
      settle: () => street.settle(),
      commit: (page) => street.travel(page === 'next' ? -1 : 1),
      turn: () => {},
    },
  };
  // The whole stage takes the swipe — sky, street and building alike.
  attachSwipe(stage, frame.swipe);
  recentreOnResize(frame);
  frames.set(screen, frame);
  return frame;
}

export function renderHome(screen: HTMLElement, view: HomeView, actions: HomeActions): void {
  const building = view.model.buildings[view.viewing];
  if (!building) return;
  const frame = frameFor(screen);
  screen.dataset.fresh = String(view.model.fresh);
  screen.dataset.building = building.def.id;

  const header = top(building, view, actions);
  frame.header.replaceWith(header);
  frame.header = header;
  // The build stamp tells a stale tunnel from a fresh deploy at a glance
  // (CLAUDE.md), so it stays on screen, out of the way, in a corner.
  frame.stamp.textContent = `build ${view.stamp}`;

  const { lot, gap } = lotFor(building, view, actions);
  const scroll = el('div', { class: 'home-scroll' }, [lot]);
  frame.scroll?.remove();
  frame.street.street.before(scroll);
  frame.scroll = scroll;
  frame.stage.dataset.underground = String(gap !== null);
  rideStreet(frame, scroll, gap);

  // The building either side, by swipe or by the tab on that edge. One not
  // reached yet can be looked at, dark, from the first launch on: what is
  // coming, kept a mystery (amending ADR-0019).
  const neighbour = (step: number): HomeBuilding | undefined =>
    view.model.buildings[view.viewing + step];
  const next = neighbour(1);
  frame.street.setBuilding(
    building.def.id,
    building.reached && next && !next.reached && !view.model.fresh ? next.def.name : null,
  );
  frame.swipe.canPrev = neighbour(-1) !== undefined;
  frame.swipe.canNext = next !== undefined;
  frame.swipe.turn = (page) => actions.view(view.viewing + (page === 'next' ? 1 : -1));
  if (view.enter) {
    scroll.dataset.enter = view.enter;
    // A tab or a sign walks the street too; a swipe already started walking.
    if (!frame.street.travelling) frame.street.travel(view.enter === 'from-right' ? -1 : 1);
  }

  for (const tab of frame.stage.querySelectorAll('.home-edge, .home-locked')) tab.remove();
  if (!building.reached) frame.stage.append(lockedBadge(view.model.buildings[view.viewing - 1]));
  for (const step of [-1, 1]) {
    const to = neighbour(step);
    if (!to) continue;
    const tab = el(
      'button',
      {
        class: 'home-edge',
        type: 'button',
        'data-side': step < 0 ? 'prev' : 'next',
        'data-locked': String(!to.reached),
        'aria-label': to.reached ? to.def.name : 'A building still locked',
      },
      [icon(step < 0 ? 'back' : 'forward')],
    );
    tab.addEventListener('click', () => actions.view(view.viewing + step));
    frame.stage.append(tab);
  }

  requestAnimationFrame(() => centreOnNext(scroll));
}

/**
 * Open on the floor that matters: the next shift, or the top of what is lit.
 * Measured rather than `scrollIntoView`, which would also slide the screen
 * sideways to meet a building still arriving from the edge.
 */
function centreOnNext(scroll: HTMLElement): void {
  const focus =
    scroll.querySelector<HTMLElement>('.floor[data-state="next"]') ??
    [...scroll.querySelectorAll<HTMLElement>('.floor[data-state="played"]')][0] ??
    null;
  if (!focus) {
    scroll.scrollTop = scroll.scrollHeight;
  } else {
    const at = focus.getBoundingClientRect();
    const box = scroll.getBoundingClientRect();
    scroll.scrollTop += at.top - box.top - (box.height - at.height) / 2;
  }
  // The street rides on the scroll (`rideStreet`); a new size moves it even
  // when the scroll position stays.
  scroll.dispatchEvent(new Event('scroll'));
}

/**
 * Open on the next shift again after a rotation or a new window size: a phone
 * turned sideways left Play under the street until the player scrolled
 * (qa-lead review, 2026-10-26). An address bar coming and going does not.
 */
function recentreOnResize(frame: Frame): void {
  if (typeof ResizeObserver !== 'function') return;
  let size: Size = { width: 0, height: 0 };
  new ResizeObserver(([entry]) => {
    if (!entry) return;
    const next = { width: entry.contentRect.width, height: entry.contentRect.height };
    const recentre = recentresAfter(size, next);
    size = next;
    if (recentre && frame.scroll) centreOnNext(frame.scroll);
  }).observe(frame.stage);
}

/**
 * The top of the home screen is the same lintel as the top of play: walnut, a
 * brass edge, brass call buttons, and an indicator window — here for coins.
 */
function top(building: HomeBuilding, view: HomeView, actions: HomeActions): HTMLElement {
  const { model } = view;
  const settings = callButton('cog', 'Settings', actions.settings);
  if (model.fresh) {
    // Nothing has happened yet, so there is nothing to report: a title, and
    // the building waiting underneath it.
    return el('header', { class: 'home-top lintel', 'data-fresh': 'true' }, [
      el('div', { class: 'home-title' }, [
        el('h1', { text: 'Full Elevator' }),
        el('p', { text: 'One lift. Everyone wants a different floor.' }),
      ]),
      settings,
    ]);
  }

  const first = building.floors[0];
  const last = building.floors[building.floors.length - 1];
  const sub = !building.reached
    ? 'Locked'
    : first && last
      ? `${first.number}–${last.number} · ${building.stars}/${building.floors.length * 3} ★`
      : '';
  // Which building, and how it is going. Paging lives on the screen's edges.
  const place = el('div', { class: 'home-place' }, [
    // Without its article, as on the roof sign: the lintel is short of room.
    el('div', { class: 'home-building', text: building.def.name.replace(/^The /, '') }),
    el('div', { class: 'home-sub', text: sub }),
  ]);

  // Coins sit in the lift's indicator window; the garage has its own button
  // beside them, which wears a dot when something there is affordable.
  const coins = el('button', { class: 'home-coins', type: 'button', 'aria-label': 'Coins' }, [
    coinIcon(),
    el('span', { text: String(view.coins) }),
  ]);
  coins.addEventListener('click', actions.garage);
  const upgrade = el('button', { class: 'brass-pill home-upgrade', type: 'button' }, [
    icon('wrench'),
    el('span', { class: 'brass-pill-text', text: 'Upgrade' }),
  ]);
  upgrade.dataset.ready = String(view.garageReady);
  upgrade.dataset.lesson = String(view.lessonDue === true);
  const ready = view.garageReady ? ' — a part is affordable' : '';
  upgrade.setAttribute('aria-label', `Upgrade${ready}`);
  upgrade.addEventListener('click', actions.garage);

  return el('header', { class: 'home-top lintel' }, [settings, place, coins, upgrade]);
}

/** The building's crown: its own sign, roof and rooftop (ui/facades.ts). */
function roof(building: HomeBuilding): HTMLElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 340 88');
  svg.setAttribute('preserveAspectRatio', 'xMidYMax meet');
  svg.setAttribute('class', 'roof-art');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = crownSvg(building.def.id);
  return el('div', { class: 'b-roof' }, [svg]);
}

function floors(
  building: HomeBuilding,
  view: HomeView,
  actions: HomeActions,
  part: 'above' | 'below',
): HTMLElement {
  const stack = el('div', { class: 'b-floors' });
  // How high each floor stands: its place above the street, or its depth under it.
  const height = (floor: HomeFloor, i: number) => floor.depth ?? i;
  const next = building.floors.findIndex((f) => f.state === 'next');
  const parked = next >= 0 ? height(building.floors[next] as HomeFloor, next) : null;
  // Top floor first: a building is read from the roof down, and its basement
  // from the street down.
  const shown = building.floors
    .map((floor, i) => ({ floor, high: height(floor, i) }))
    .filter(({ floor }) => (floor.depth === null) === (part === 'above'))
    .sort((a, b) => b.high - a.high);
  for (const { floor, high } of shown) {
    // The cable runs from the roof down to wherever the lift is parked.
    stack.append(floorRow(floor, parked !== null && high > parked, view, actions));
  }
  return stack;
}

/** Beside the stars: the bonus medal, lit once won, an outline to come back for. */
function addMedal(row: HTMLElement, room: HTMLElement, medal: HomeFloor['medal']): void {
  if (!medal) return;
  row.dataset.medal = medal;
  const holder = el('span', {
    class: 'floor-medal',
    'aria-label': medal === 'met' ? 'Bonus met' : 'Bonus not met yet',
  });
  holder.append(medalSvg(medal === 'met'));
  room.append(holder);
}

function floorRow(
  floor: HomeFloor,
  cable: boolean,
  view: HomeView,
  actions: HomeActions,
): HTMLElement {
  const open = floor.state !== 'locked';
  const shift = `Shift ${floor.number}, ${floor.level.name}`;
  const row = el(open ? 'button' : 'div', {
    class: floor.level.hard ? 'floor floor-is-hard' : 'floor',
    type: open ? 'button' : undefined,
    'data-state': floor.state,
    'data-room': floor.room,
    // What the lift zooms into when this shift starts (ADR-0057).
    'data-level': floor.level.id,
    'aria-label': floor.state === 'next' ? `Play ${shift}` : shift,
  });
  if (open) row.addEventListener('click', () => actions.play(floor.level));

  const shaft = el('span', { class: 'floor-shaft', 'data-cable': String(cable) });
  // Under the street the shaft wall says how far down, as a real one is painted.
  if (floor.depth !== null) {
    shaft.append(el('span', { class: 'floor-depth', text: floorName(floor.depth, 0) }));
  }
  if (floor.state === 'next') shaft.append(lift(view.loadout, floor.level.buildingId));

  const room = el('span', { class: 'floor-room' }, [roomSvg(floor.room)]);
  const label = el('span', { class: 'floor-label' }, [
    el('span', { class: 'plaque', text: String(floor.number).padStart(2, '0') }),
    el('span', { class: 'floor-name', text: floor.level.name }),
    // A hard shift says so before anyone plays it (ADR-0037).
    ...(floor.level.hard ? [el('span', { class: 'floor-hard', text: 'HARD' })] : []),
    // Someone not met yet, on the shift on offer: a badge on the plaque, so the
    // name keeps its room.
    ...(floor.newFace && floor.state === 'next'
      ? [el('span', { class: 'floor-new-badge', text: 'NEW' })]
      : []),
  ]);
  room.append(label);

  if (floor.state === 'played') {
    room.append(
      el('span', {
        class: 'floor-stars',
        text: '★'.repeat(floor.stars) + '☆'.repeat(3 - floor.stars),
      }),
    );
    addMedal(row, room, floor.medal);
  } else if (floor.state === 'next') {
    // Play sits on the shift it starts. The whole floor is the button; this is
    // what says so.
    room.append(el('span', { class: 'floor-play', 'aria-hidden': 'true' }, [playIcon(), 'Play']));
  } else if (floor.state === 'locked' && floor.after) {
    room.append(el('span', { class: 'floor-after', text: `opens after ${floor.after}` }));
  }
  // Someone not met yet (ADR-0037): a silhouette with a question for a ticket.
  if (floor.newFace && floor.state !== 'next') {
    const mystery = el('span', { class: 'floor-new', 'aria-label': 'Someone new' });
    mystery.innerHTML = SILHOUETTE;
    mystery.append(el('span', { class: 'floor-new-tag', text: 'someone new' }));
    room.append(mystery);
  }
  if (floor.state === 'next') {
    const people = el('span', { class: 'floor-people' });
    for (const person of previewFor(floor.level)) {
      const holder = el('span', { class: 'floor-person' });
      holder.innerHTML = personSvg(
        person.id,
        person.traits,
        person.destination,
        person.nextStop,
        groundOf(floor.level.buildingId),
      );
      people.append(holder);
    }
    room.append(people);
  }
  row.append(shaft, room);
  return row;
}

/** Someone not met yet, waiting where they will first appear: a shape holding a question. */
const SILHOUETTE =
  '<svg viewBox="0 0 36 64" width="22" height="39" aria-hidden="true">' +
  '<circle cx="18" cy="11" r="8" fill="currentColor"/>' +
  '<path d="M7 62V34a11 11 0 0 1 22 0v28z" fill="currentColor"/>' +
  '<rect x="4" y="30" width="28" height="20" rx="7" fill="#f4ead6" stroke="currentColor" stroke-width="2"/>' +
  '<text x="18" y="45" text-anchor="middle" font-family="Baloo 2, system-ui, sans-serif" font-size="15" font-weight="800" fill="currentColor">?</text>' +
  '</svg>';

/** Each building's door finish on the home screen's lift, by tier (ADR-0027). */
const HOME_DOORS: Readonly<Record<LiftLook, readonly [string, string, string]>> = {
  steel: ['#e8d8ba', '#d9cbb4', '#c49a52'],
  brass: ['#e3c48a', '#c49a52', '#c49a52'],
  wide: ['#e8d8ba', '#d9cbb4', '#d9e4e6'],
  glass: ['#d9e4e6', '#d9e4e6', '#d9e4e6'],
};

/** The lift, parked with its doors open, wearing the parts fitted to this building's lift. */
function lift(loadout: Loadout, buildingId: string | undefined): HTMLElement {
  const motor = loadout.motor ?? 0;
  const doors = Math.min(2, loadout.doors ?? 0);
  const look = liftLookOf(buildingId);
  const roofMotor =
    motor === 0
      ? ''
      : motor === 1
        ? '<circle cx="22" cy="5" r="4.5" fill="#c49a52" stroke="#3d2c1e" stroke-width="1.2"/><circle cx="22" cy="5" r="1.5" fill="#3d2c1e"/>'
        : '<rect x="12" y="0" width="20" height="8" rx="4" fill="#3f3a36" stroke="#3d2c1e" stroke-width="1.2"/><circle cx="27" cy="4" r="1.8" fill="#ffd27a"/>';
  const door = HOME_DOORS[look][doors];
  // The finish's mark on the open doors: the station's hazard edge, the
  // tower's light, the hotel's brass seam.
  const edge =
    look === 'wide'
      ? '<path d="M9 16v52M35 16v52" stroke="#ffd27a" stroke-width="2"/>'
      : look === 'glass' && doors === 2
        ? '<path d="M9.5 16v52M34.5 16v52" stroke="#7fd0e6" stroke-width="1.6"/>'
        : look === 'brass'
          ? '<path d="M7.5 16v52M36.5 16v52" stroke="#8a6a2e" stroke-width="0.8"/>'
          : '';
  const box = el('span', { class: 'lift', 'aria-hidden': 'true', 'data-look': look });
  box.innerHTML = `<svg viewBox="0 0 44 74" overflow="visible">
    <path d="M19.5 -26V8M24.5 -26V8" stroke="#8a8279" stroke-width="1.8"/>
    <path d="M6 13 L22 7 L38 13 Z" fill="#3d2c1e"/>
    ${roofMotor}
    <rect x="2" y="12" width="40" height="60" rx="7" fill="#fffaf0" stroke="#3d2c1e" stroke-width="2.5"/>
    <rect x="5" y="15" width="5" height="54" rx="1.5" fill="${door}" stroke="#3d2c1e" stroke-width="1"/>
    <rect x="34" y="15" width="5" height="54" rx="1.5" fill="${door}" stroke="#3d2c1e" stroke-width="1"/>
    ${edge}
  </svg>`;
  return box;
}

/** The ground floor: not a shift, the way in — the building's own front door. */
function entrance(building: HomeBuilding): HTMLElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 340 60');
  svg.setAttribute('preserveAspectRatio', 'xMidYMax meet');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = entranceSvg(building.def.id);
  return el('div', { class: 'b-entrance' }, [svg]);
}

function playIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 12 12');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = '<path d="M3 1.6v8.8L10.4 6z" fill="currentColor" stroke-linejoin="round"/>';
  return svg;
}

/**
 * Over a building not reached yet: a padlock, and what opens it. The building
 * itself stays a dark shape, its shifts unnamed, until then.
 */
function lockedBadge(before: HomeBuilding | undefined): HTMLElement {
  const lock = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  lock.setAttribute('viewBox', '0 0 32 36');
  lock.setAttribute('aria-hidden', 'true');
  lock.innerHTML =
    '<path d="M9 16v-5a7 7 0 0 1 14 0v5" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/>' +
    '<rect x="4" y="15" width="24" height="18" rx="4" fill="currentColor"/>' +
    '<circle cx="16" cy="23" r="2.6" fill="#2a2030"/><path d="M16 24v4" stroke="#2a2030" stroke-width="2.4" stroke-linecap="round"/>';
  return el('div', { class: 'home-locked', role: 'note' }, [
    lock,
    el('div', {
      class: 'home-locked-line',
      text: before ? `Opens after the ${before.def.name.replace(/^The /, '')}` : 'Not open yet',
    }),
  ]);
}

/**
 * The building on its lot. It stands on the street: a short one sits on it with
 * sky above, a tall one scrolls with its lobby meeting the pavement at the
 * bottom. One with floors under the street (ADR-0028) goes on below it: a gap
 * the street itself fills, then the ground with those floors in it.
 */
function lotFor(
  building: HomeBuilding,
  view: HomeView,
  actions: HomeActions,
): { lot: HTMLElement; gap: HTMLElement | null } {
  const lot = el('div', { class: 'home-lot', 'data-locked': String(!building.reached) }, [
    el('div', { class: 'building', 'data-building': building.def.id }, [
      roof(building),
      floors(building, view, actions, 'above'),
      entrance(building),
    ]),
  ]);
  if (!building.floors.some((f) => f.depth !== null)) return { lot, gap: null };
  const gap = el('div', { class: 'b-street-gap', 'aria-hidden': 'true' });
  lot.append(gap, underground(building, view, actions));
  return { lot, gap };
}

/**
 * The ground under the street (ADR-0028), with the building's floors that are
 * in it: the mains and roots just under the pavement, the floors, then the
 * metro tunnel with a train through it now and then. Full width, as the street
 * is; the floors stay in the building's own column.
 */
function underground(building: HomeBuilding, view: HomeView, actions: HomeActions): HTMLElement {
  const utilities = el('div', { class: 'under-utilities', 'aria-hidden': 'true' });
  utilities.innerHTML = UTILITIES;
  const drip = el('span', { class: 'under-drip', 'aria-hidden': 'true' });
  drip.innerHTML = DRIP;
  const worm = el('span', { class: 'under-worm', 'aria-hidden': 'true' });
  worm.innerHTML = WORM_ART;
  const tunnel = el('div', { class: 'under-tunnel', 'aria-hidden': 'true' });
  tunnel.innerHTML = TUNNEL_ART;
  const metro = el('div', { class: 'metro' });
  metro.innerHTML = METRO;
  tunnel.append(metro);
  return el('div', { class: 'b-underground' }, [
    utilities,
    drip,
    worm,
    el('div', { class: 'building b-below', 'data-building': building.def.id }, [
      floors(building, view, actions, 'below'),
    ]),
    tunnel,
  ]);
}

/**
 * The street rides up with the ground line (ADR-0028). It is one persistent
 * strip, never redrawn, so its people and traffic keep moving; a building with
 * floors under it lifts the strip to sit on the gap left for it, and the ground
 * shows below. Above that, the street stays where it always is, along the foot
 * of the screen.
 */
function rideStreet(frame: Frame, scroll: HTMLElement, gap: HTMLElement | null): void {
  const street = frame.street.street;
  if (!gap) {
    street.style.transform = '';
    return;
  }
  const place = (): void => {
    const stage = frame.stage.getBoundingClientRect();
    const rest = stage.bottom - street.offsetHeight;
    const lift = Math.min(0, gap.getBoundingClientRect().top - rest);
    street.style.transform = lift < 0 ? `translateY(${lift}px)` : '';
  };
  scroll.addEventListener('scroll', place, { passive: true });
  requestAnimationFrame(() => requestAnimationFrame(place));
}
